/* =============================================================
   PartnerExchangeHistory.jsx — Exchange history for a partner
   -------------------------------------------------------------
   Dynamic exchange history page for any partner university,
   accessible via /analytics/partner/:id.

   Data sources:
     usePartnerById(:id)  — partner name, country, type
     Supabase query       — all students with partner_id = :id
                            grouped by year to build history rows
     useLocalNote()       — admin notes from Supabase notes table

   Exchange history is calculated directly from the students
   table — no extra DB columns needed:
     Sent     = outgoing students (ESMT → partner)
     Received = incoming students (partner → ESMT)
   Grouped by EXTRACT(YEAR FROM created_at).
   ============================================================= */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams }        from 'react-router-dom';
import Layout             from '../components/Layout';
import { supabase }       from '../lib/supabase';
import { usePartnerById } from '../hooks/usePartnerById';
import { useLocalNote }   from '../hooks/useLocalNote';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';


/* ─── HELPERS ────────────────────────────────────────────────── */
function balanceBadge(b) {
  if (b > 0) return { label: `+${b}`, cls: 'bg-emerald-100 text-emerald-700' };
  if (b < 0) return { label: `${b}`,  cls: 'bg-red-100 text-red-600'         };
  return       { label: 'Balanced',   cls: 'bg-slate-100 text-slate-500'     };
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0b1c30] text-white text-xs rounded-xl shadow-xl px-4 py-3 space-y-1">
      <p className="font-bold text-sm mb-1">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ background: entry.fill }} />
          <span className="text-slate-300">{entry.name}:</span>
          <span className="font-semibold">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

const CONN_CONFIG = {
  Connected:      { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'Guest Portal': { bg: 'bg-sky-100',     text: 'text-sky-700',     dot: 'bg-sky-500'     },
  Manual:         { bg: 'bg-slate-100',   text: 'text-slate-500',   dot: ''               },
};


/* ─── COMPONENT ──────────────────────────────────────────────── */
export default function PartnerExchangeHistory() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const { partner, loading: partnerLoading } = usePartnerById(id);
  const { note, setNote, save, saving: noteSaving, savedAt, justSaved } = useLocalNote(`partner_${id}`);

  const [students,   setStudents]   = useState([]);
  const [histLoading, setHistLoading] = useState(true);


  /* ── Fetch all students for this partner ─────────────────── */
  useEffect(() => {
    if (!id) return;
    setHistLoading(true);
    (async () => {
      try {
        const { data, error } = await supabase
          .from('students')
          .select('type, created_at')
          .eq('partner_id', id);
        if (error) console.error('[PartnerExchangeHistory] fetch:', error.message);
        setStudents(data ?? []);
      } catch (err) {
        console.error('[PartnerExchangeHistory] unexpected:', err);
      } finally {
        setHistLoading(false);
      }
    })();
  }, [id]);


  /* ── Build history rows from students ────────────────────── */
  const currentYear = new Date().getFullYear().toString();

  const history = useMemo(() => {
    const byYear = {};
    students.forEach(s => {
      const year = new Date(s.created_at).getFullYear().toString();
      if (!byYear[year]) byYear[year] = { year, sent: 0, received: 0 };
      if (s.type === 'outgoing') byYear[year].sent++;
      else                       byYear[year].received++;
    });
    return Object.values(byYear)
      .sort((a, b) => Number(a.year) - Number(b.year))
      .map(r => ({
        ...r,
        balance: r.sent - r.received,
        current: r.year === currentYear,
      }));
  }, [students, currentYear]);

  const totalSent     = history.reduce((s, r) => s + r.sent,     0);
  const totalReceived = history.reduce((s, r) => s + r.received, 0);
  const overallBal    = totalSent - totalReceived;
  const yearsActive   = history.length;
  const chartData     = history.map(r => ({ year: r.year, Sent: r.sent, Received: r.received }));


  /* ── CSV export ──────────────────────────────────────────── */
  const exportCSV = () => {
    const headers = ['Year', 'Sent', 'Received', 'Balance'];
    const rows = history.map(r => [
      r.year + (r.current ? ' (current)' : ''),
      r.sent,
      r.received,
      r.balance > 0 ? `+${r.balance}` : r.balance,
    ]);
    const csv  = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `exchange_history_${partner?.abbreviation ?? id}_unixplore.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const conn = CONN_CONFIG[partner?.connection] ?? CONN_CONFIG.Manual;

  return (
    <Layout>
      {/* Back button */}
      <button
        onClick={() => navigate('/analytics')}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600 mb-6 transition-colors group"
      >
        <span className="material-symbols-outlined text-[18px] group-hover:-translate-x-0.5 transition-transform">arrow_back</span>
        Exchange Analytics
      </button>

      {/* Page header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          {partnerLoading ? (
            <div className="animate-pulse space-y-2">
              <div className="h-8 bg-slate-100 rounded w-64" />
              <div className="h-4 bg-slate-100 rounded w-40" />
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl font-semibold text-[#0b1c30]">{partner?.name ?? '—'}</h1>
              <p className="text-[#464555] mt-2 flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[16px]">location_on</span>
                {partner?.country} · {partner?.type}
              </p>
            </>
          )}
        </div>
        {partner && (
          <span className={`flex items-center gap-1.5 text-xs font-semibold ${conn.bg} ${conn.text} px-3 py-1.5 rounded-full`}>
            {conn.dot && <span className={`w-2 h-2 rounded-full ${conn.dot}`} />}
            {partner.connection}
          </span>
        )}
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* ── Left column ───────────────────────────────────────── */}
        <div className="col-span-8 space-y-6">

          {/* Year-by-year table */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="font-display text-base font-semibold text-[#0b1c30]">Exchange History</h2>
                <p className="text-xs text-slate-400 mt-0.5">All time data with {partner?.name ?? '…'}</p>
              </div>
              {history.length > 0 && (
                <button
                  onClick={exportCSV}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-500 text-xs font-medium rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <span className="material-symbols-outlined text-[14px]">download</span>
                  Export CSV
                </button>
              )}
            </div>

            <div className="p-6">
              {histLoading ? (
                <div className="animate-pulse space-y-3">
                  {[1,2,3,4].map(i => <div key={i} className="h-10 bg-slate-100 rounded" />)}
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-8">
                  <span className="material-symbols-outlined text-slate-200 text-[36px]">history</span>
                  <p className="text-sm text-slate-400 mt-2">No exchange history yet</p>
                </div>
              ) : (
                <>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide pb-3">Year</th>
                        <th className="text-center text-xs font-semibold text-slate-400 uppercase tracking-wide pb-3">Sent</th>
                        <th className="text-center text-xs font-semibold text-slate-400 uppercase tracking-wide pb-3">Received</th>
                        <th className="text-center text-xs font-semibold text-slate-400 uppercase tracking-wide pb-3">Balance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {history.map((row, i) => {
                        const badge = balanceBadge(row.balance);
                        return (
                          <tr key={i} className={row.current ? 'bg-slate-50/60' : ''}>
                            <td className="py-3 text-sm font-medium text-[#0b1c30]">
                              {row.year}
                              {row.current && <span className="ml-2 text-xs text-slate-400 font-normal">current</span>}
                            </td>
                            <td className="py-3 text-center text-sm text-[#464555]">{row.sent}</td>
                            <td className="py-3 text-center text-sm text-[#464555]">{row.received}</td>
                            <td className="py-3 text-center">
                              <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <p className="text-xs text-slate-400 mt-4">Negative balance = received more students than sent</p>
                </>
              )}
            </div>
          </div>

          {/* Trend chart */}
          {history.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
                <span className="material-symbols-outlined text-[18px] text-indigo-600">show_chart</span>
                <h2 className="font-display text-base font-semibold text-[#0b1c30]">Exchange Trend</h2>
              </div>
              <div className="p-6">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={chartData} barCategoryGap="32%" barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#94a3b8', fontFamily: 'Inter, sans-serif' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: '#94a3b8', fontFamily: 'Inter, sans-serif' }} axisLine={false} tickLine={false} width={24} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc' }} />
                    <Legend wrapperStyle={{ paddingTop: '14px', fontSize: '13px', fontFamily: 'Inter, sans-serif' }} formatter={(v) => <span style={{ color: '#475569' }}>{v}</span>} />
                    <Bar dataKey="Sent"     fill="#4343d5" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="Received" fill="#cbd5e1" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        {/* ── Right column ──────────────────────────────────────── */}
        <div className="col-span-4 space-y-6">

          {/* Partnership Summary */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px] text-indigo-600">summarize</span>
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Partnership Summary</h2>
            </div>
            <div className="p-6 space-y-5">
              {[
                { icon: 'flight_takeoff', bg: 'bg-indigo-50', color: 'text-indigo-600', label: 'Total Sent',     value: totalSent,     valueColor: 'text-indigo-600' },
                { icon: 'flight_land',   bg: 'bg-slate-100',  color: 'text-slate-500',  label: 'Total Received', value: totalReceived, valueColor: 'text-slate-500'  },
                { icon: 'calendar_month',bg: 'bg-indigo-50',  color: 'text-indigo-600', label: 'Years Active',   value: yearsActive,   valueColor: 'text-indigo-600' },
              ].map(({ icon, bg, color, label, value, valueColor }) => (
                <div key={label} className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center`}>
                      <span className={`material-symbols-outlined ${color} text-[16px]`}>{icon}</span>
                    </div>
                    <span className="text-sm text-[#464555]">{label}</span>
                  </div>
                  <span className={`font-display text-xl font-bold ${valueColor}`}>{value}</span>
                </div>
              ))}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${overallBal === 0 ? 'bg-slate-100' : overallBal > 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                    <span className={`material-symbols-outlined text-[16px] ${overallBal === 0 ? 'text-slate-500' : overallBal > 0 ? 'text-emerald-600' : 'text-red-500'}`}>balance</span>
                  </div>
                  <div>
                    <span className="text-sm text-[#464555]">Overall Balance</span>
                    <p className="text-xs text-slate-400 mt-0.5">{overallBal === 0 ? 'Balanced' : overallBal > 0 ? 'Sending more' : 'Receiving more'}</p>
                  </div>
                </div>
                <span className={`font-display text-xl font-bold ${overallBal === 0 ? 'text-slate-400' : overallBal > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {overallBal > 0 ? `+${overallBal}` : overallBal}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <h2 className="font-display text-base font-semibold text-[#0b1c30]">Actions</h2>
            </div>
            <div className="p-5 space-y-3">
              <button
                onClick={() => navigate(`/partners/${id}`)}
                className="w-full py-2.5 bg-[#4343d5] text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined text-[16px]">account_balance</span>
                View Partner Profile
              </button>
              {history.length > 0 && (
                <button
                  onClick={exportCSV}
                  className="w-full py-2.5 border border-indigo-300 text-indigo-600 rounded-xl text-sm font-medium hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2"
                >
                  <span className="material-symbols-outlined text-[16px]">download</span>
                  Export Full History
                </button>
              )}
              {partner?.contact_email && (
                <a
                  href={`mailto:${partner.contact_email}`}
                  className="w-full py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
                >
                  <span className="material-symbols-outlined text-[16px]">send</span>
                  Send Message to IO
                </a>
              )}
            </div>
          </div>

          {/* Admin Notes */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-[18px] text-indigo-600">notes</span>
                <h2 className="font-display text-base font-semibold text-[#0b1c30]">Admin Notes</h2>
              </div>
              {savedAt && (
                <p className="text-[11px] text-slate-400 flex items-center gap-1">
                  <span className="material-symbols-outlined text-[12px]">schedule</span>
                  {savedAt}
                </p>
              )}
            </div>
            <div className="p-5">
              <p className="text-xs text-slate-400 mb-3">Internal notes — visible only to university staff.</p>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                rows={3}
                placeholder="Add a note about this partnership..."
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-300 text-[#0b1c30] placeholder-slate-400 transition"
              />
              <button
                onClick={save}
                disabled={noteSaving}
                className={`mt-3 w-full py-2.5 rounded-xl text-sm font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-60 ${
                  justSaved
                    ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                    : 'border border-slate-200 text-[#464555] hover:bg-slate-50'
                }`}
              >
                <span className="material-symbols-outlined text-[16px]">{justSaved ? 'check_circle' : 'save'}</span>
                {justSaved ? 'Saved!' : noteSaving ? 'Saving…' : 'Save Note'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
